// Recupera al visitante cuando un chunk lazy ya no existe en el deploy actual.
//
// EL FALLO, medido en PostHog 229664 (30-jul a 01-ago 2026): 4 de las 6 excepciones
// de esa semana eran el mismo problema con tres mensajes distintos, porque cada
// motor lo nombra diferente:
//   - "'text/html' is not a valid JavaScript MIME type."      (Safari)
//   - "Failed to fetch dynamically imported module: .../FounderConstellation-B7tWTmQe.js"
//   - "Importing a module script failed."
//
// EL MECANISMO: index.html se sirve con max-age=0, los chunks con hash se sirven
// inmutables. Una pestana abierta desde antes de un deploy sigue teniendo el HTML
// viejo en memoria, y cuando el usuario baja hasta un componente lazy pide un hash
// que Cloudflare Pages ya no tiene. Pages responde el index.html con **200**, no 404
// (verificado en vivo el 2026-08-03: /assets/FounderConstellation-B7tWTmQe.js
// devuelve `200 text/html`), asi que el navegador recibe HTML donde esperaba un
// modulo y el import revienta.
//
// POR QUE EL MOUNT GUARD DE vite.config.ts NO LO CUBRE: ese guard vigila que React
// MONTE, y aca React monta perfecto. El fallo llega despues, cuando un import()
// tardio no resuelve. Su poll de mounted() ve la app viva y limpia su marca. Son
// dos fallos distintos con la misma cura (una recarga) y hacian falta los dos.
//
// POR QUE NO SE ARREGLA EN CLOUDFLARE: la idea obvia era un `_redirects` con
// `/assets/* /404.html 404` para que el asset faltante devuelva 404 honesto. No se
// puede, por dos razones documentadas: (1) en Pages "redirects are always followed,
// regardless of whether or not an asset matches", asi que esa regla se comeria
// TAMBIEN los assets vivos y tumbaria el sitio entero; (2) los rewrites con codigo
// 404 no estan soportados. Un Pages Function como catchall tampoco sirve: las
// Functions corren ANTES que los assets estaticos, asi que meteria un Worker en la
// ruta critica de cada .js y .css. La recarga es la unica cura barata.
//
// POR QUE LA MARCA NO ES UN BOOLEANO (incidente del 2026-08-04): la version
// original gastaba UNA recarga por sesion de pestana y no la reponia nunca. Dos
// deploys en 15 minutos bastaron para romperla: el primero gasto la recarga, el
// segundo encontro la marca puesta, salio temprano y dejo que el error llegara a
// RouteErrorBoundary ("Algo salio mal"), cuando lo unico que hacia falta era
// recargar otra vez. Un booleano no distingue los dos casos que importan:
//   - el chunk falta DE VERDAD          -> recargar de nuevo es un bucle infinito
//   - entro OTRO deploy mientras tanto  -> recargar es exactamente la cura
// El discriminante NO puede ser el mensaje del error: dos de los tres textos
// reales ("Importing a module script failed." y el de MIME de Safari) no traen la
// url del chunk. Se usa el hash de los bundles de entrada que la pestana tiene
// cargados, que cambia en cada deploy y esta siempre en el DOM.
import { captureException } from "@/lib/analytics-events";

/** Marca de la ultima recarga por chunk obsoleto: build en el que ocurrio y cuantas van. */
export const RELOAD_KEY = "tfd_stale_chunk_reload";
/** Cuantas recuperaciones ya se reportaron, para no duplicarlas en cada carga. */
export const REPORTED_KEY = "tfd_stale_chunk_reported";
/** Limit a rapid deployment burst, rather than the lifetime of an open tab. */
export const MAX_STALE_CHUNK_RELOADS = 3;
const RELOAD_BURST_WINDOW_MS = 60_000;

interface Marker {
  message?: string;
  at?: string;
  /** Identidad del build que corria la pestana cuando fallo el import. */
  build?: string;
  count?: number;
}

interface Deps {
  target?: EventTarget;
  storage?: Storage;
  reload?: () => void;
  report?: (error: unknown, properties?: Record<string, unknown>) => void;
  buildId?: () => string;
}

/**
 * Identidad del build cargado AHORA en la pestana, leida de los <script type="module">
 * que Vite inyecta con hash de contenido. Se juntan todos en vez de buscar "main-"
 * por nombre: si el entry se renombra, esto sigue funcionando en vez de degradarse
 * en silencio. Cuando no hay ninguno (jsdom, o un DOM inesperado) devuelve "unknown",
 * que colapsa al comportamiento viejo de una sola recarga y queda visible en el
 * reporte de PostHog como build "unknown".
 */
export function currentBuildId(doc: Document = document): string {
  try {
    const srcs = Array.from(doc.querySelectorAll('script[type="module"][src]'))
      .map((s) => s.getAttribute("src") ?? "")
      .filter((src) => src.includes("/assets/"))
      .sort();
    return srcs.length > 0 ? srcs.join("|") : "unknown";
  } catch {
    return "unknown";
  }
}

/** sessionStorage tira en Safari privado y con cookies bloqueadas. Nunca dejar que eso mate la recarga. */
function safeGet(storage: Storage, key: string): string | null {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(storage: Storage, key: string, value: string): void {
  try {
    storage.setItem(key, value);
  } catch {
    /* sin persistencia solo perdemos el limite de una recarga, no la recarga */
  }
}

/** Lee la marca tolerando la forma vieja (sin build ni count) y el JSON corrupto. */
function readMarker(storage: Storage): Marker | null {
  const raw = safeGet(storage, RELOAD_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as Marker;
  } catch {
    // Marca corrupta: cuenta como una recarga ya gastada para que el tope siga valiendo.
    return { message: raw, count: 1 };
  }
}

export function installStaleChunkRecovery({
  target = window,
  storage = window.sessionStorage,
  reload = () => window.location.reload(),
  report = captureException,
  buildId = () => currentBuildId(),
}: Deps = {}): void {
  // Si venimos de una recarga por chunk obsoleto, reportarla AHORA y no antes:
  // un capture disparado justo antes de location.reload() casi nunca alcanza a
  // salir, porque posthog-js agrupa los eventos. Esta es la metrica que dice
  // cuantos visitantes reales pega cada deploy.
  //
  // Se compara contra el CONTADOR y no contra un booleano: si la misma pestana se
  // recupera dos veces (dos deploys seguidos) las dos tienen que aparecer, o un
  // deploy que pega dos veces se ve identico a uno que pega una.
  const marker = readMarker(storage);
  if (marker) {
    const recoveries = String(marker.count ?? 1);
    if (safeGet(storage, REPORTED_KEY) !== recoveries) {
      safeSet(storage, REPORTED_KEY, recoveries);
      report(new Error("Stale chunk recovered by reload"), {
        ...marker,
        recovered: true,
      });
    }
  }

  target.addEventListener("vite:preloadError", (event) => {
    const prior = readMarker(storage);
    const build = buildId();

    // Ya recargamos estando en ESTE mismo build y el import sigue fallando: el
    // chunk falta de verdad, no es una pestana vieja. Recargar otra vez seria un
    // bucle y el visitante quedaria atrapado.
    if (prior && prior.build === build) return;

    // An old tab can span many ordinary releases. Replenish its burst budget
    // after a quiet minute, while the same-build guard above still prevents
    // repeated reloads of an actually broken deployment.
    const attemptedAt = Date.now();
    const priorAt = prior?.at ? Date.parse(prior.at) : NaN;
    const newBurst = Number.isFinite(priorAt) && attemptedAt - priorAt >= RELOAD_BURST_WINDOW_MS;
    const count = newBurst ? 0 : prior?.count ?? 0;
    if (count >= MAX_STALE_CHUNK_RELOADS) return;
    if (newBurst) safeSet(storage, REPORTED_KEY, "0");

    const payload = (event as Event & { payload?: unknown }).payload;
    safeSet(
      storage,
      RELOAD_KEY,
      JSON.stringify({
        message: payload instanceof Error ? payload.message : String(payload),
        at: new Date(attemptedAt).toISOString(),
        build,
        count: count + 1,
      }),
    );

    // A proposito NO se llama preventDefault(). __vitePreload hace
    // `t().catch(a)`, y `a` solo re-lanza si el evento no fue cancelado: si lo
    // canceláramos, la promesa resolveria a undefined y React.lazy explotaria con
    // "Cannot read properties of undefined (reading 'default')", un error inventado
    // por nosotros que tapa el real. Dejandolo propagar, si la recarga no ocurre
    // (bloqueada, o ya gastada) RouteErrorBoundary muestra el mensaje de verdad.
    reload();
  });
}
